<script lang="ts">
    import { createEventDispatcher, onMount } from 'svelte'
    import cep from 'cep-promise'
    import { faSearch } from '@fortawesome/free-solid-svg-icons'
    import type { GridButton } from '../_common/z/zgrid'
    import Zinput from '../_common/z/zinput.svelte'
    import Zact from '../_common/z/zact.svelte'
    import Zframe from '../_common/z/zframe.svelte'
    import Zbutton from '../_common/z/zbutton.svelte'
    import ZContainer from '../_common/z/z-container.svelte'
    import LoadSpinner from '../_common/load-spinner.svelte'
    import { PayerDto, UpdatePayerDto } from '../../modules/paymentsApi/purchases/dto/payer.dto'

    // ##############################################################################################################
    const dispatch = createEventDispatcher()

    export let purchaseId: string

    let payer: PayerDto = null
    let entity: UpdatePayerDto = new UpdatePayerDto()
    let show = true
    let saving = false

    async function load() {
        try {
            show = true
            payer = await window.api.payments.purchases.getPayer(purchaseId)
            if (payer) {
                entity = {
                    name: payer.name,
                    email: payer.email,
                    phone: payer.phone,
                    addressCEP: payer.addressCEP,
                    addressStreet: payer.addressStreet,
                    addressNumber: payer.addressNumber,
                    addressNeighborhood: payer.addressNeighborhood,
                    addressCity: payer.addressCity,
                    addressState: payer.addressState,
                    addressComplement: payer.addressComplement,
                }
            }
        } catch (error) {
            window.error(error.message || error)
        } finally {
            show = false
        }
    }

    onMount(async () => await load())

    // O gateway atual é quem diz o que não pode mudar — a lista vem da api, do adapter dele. No
    // Mercado Pago o e-mail identifica o cliente e o update lá recusa a requisição inteira quando o
    // campo vem junto, mesmo sem alteração.
    function travado(campo: string): boolean {
        return (payer?.immutableFields || []).includes(campo)
    }

    // Mesma integração do cadastro de pessoa (person-edit): o CEP preenche logradouro, bairro,
    // cidade e estado. Aqui ela vale ainda mais — o boleto do Mercado Pago exige justamente esses
    // campos, e digitá-los à mão é onde nasce o endereço incompleto que faz a emissão ser recusada.
    async function searchAddress() {
        const limpo = (entity.addressCEP || '').replace(/[^0-9]/g, '')
        if (limpo.length !== 8) {
            window.error('CEP inválido')
            return
        }
        try {
            const res = await cep(limpo)
            if (!res) return
            // só sobrescreve o que voltou preenchido: o número e o complemento não vêm do CEP, e
            // um retorno parcial não pode apagar o que já estava certo
            entity.addressStreet = res.street || entity.addressStreet
            entity.addressNeighborhood = res.neighborhood || entity.addressNeighborhood
            entity.addressCity = res.city || entity.addressCity
            entity.addressState = res.state || entity.addressState
        } catch (error) {
            // o person-edit engole essa falha em silêncio; aqui avisamos, senão o operador fica
            // olhando para campos vazios sem saber se o CEP não existe ou se o serviço caiu
            window.error('Não foi possível localizar este CEP.')
        }
    }

    // nomes de campo nunca aparecem crus na tela
    const ROTULOS: Record<string, string> = { email: 'e-mail', phone: 'telefone', name: 'nome', document: 'CPF / CNPJ' }
    function rotulos(campos: string[]): string {
        return (campos || []).map((campo) => ROTULOS[campo] || campo).join(', ')
    }

    // Campos que o gateway só aceita gravar uma vez e que ainda estão livres agora — vazios, ou o
    // cadastro ainda vai ser criado neste gateway. Depois do salvamento não mudam mais: no Mercado
    // Pago um e-mail errado exigiria recriar o cliente.
    function unicaChance(p: PayerDto): string[] {
        const travados = p?.immutableFields || []
        return (p?.gatewayImmutableFields || []).filter((campo) => !travados.includes(campo))
    }

    const btnCep: GridButton = { label: '', icon: faSearch, callback: searchAddress }

    async function goSave() {
        try {
            saving = true
            // O salvamento vai ao gateway ANTES de gravar aqui: se ele recusar, nada muda dos dois
            // lados e a mensagem que aparece é a dele.
            payer = await window.api.payments.purchases.updatePayer(purchaseId, entity)
            window.alert('Dados do pagador atualizados.')
            dispatch('close')
        } catch (error) {
            window.error(error.message || error)
        } finally {
            saving = false
        }
    }
</script>

<div class="root">
    <Zact>
        <div class="spacer" />
        {#if window.store.token.permissions.pay.entries.write && payer}
            <Zbutton label="Salvar" color="green" loading={saving} disabled={saving} on:click={goSave}></Zbutton>
        {/if}
    </Zact>

    {#if !show}
        {#if !payer}
            <div class="alert alert-warning text-center m-3">
                Esta cobrança ainda não tem um cadastro de pagador. Ele é criado na primeira emissão feita por aqui.
            </div>
        {:else}
            <div class="alert alert-primary m-3">
                Ao salvar, os dados são enviados ao gateway desta cobrança antes de serem gravados. Se ele recusar, nada é alterado.
            </div>

            <Zframe title="Dados do pagador" class="m-3">
                <ZContainer gapRow="8px" gapCol="8px">
                    <!-- documento é a identidade da pessoa: trocá-lo seria trocar de pagador, não editar este -->
                    <Zinput size={4} sizeM={12} label="CPF / CNPJ" type="text" value={payer.document} disabled={true}></Zinput>
                    <Zinput size={8} sizeM={12} label="Nome" type="text" bind:value={entity.name}></Zinput>

                    <Zinput size={6} sizeM={12} label="E-mail" type="text" bind:value={entity.email} disabled={travado('email')}></Zinput>
                    <Zinput size={6} sizeM={12} label="Telefone" type="text" bind:value={entity.phone} disabled={travado('phone')}></Zinput>

                    <Zinput size={4} sizeM={12} label="CEP" type="text" bind:value={entity.addressCEP} acts={[btnCep]}></Zinput>
                    <Zinput size={8} sizeM={12} label="Logradouro" type="text" bind:value={entity.addressStreet}></Zinput>
                    <Zinput size={3} sizeM={12} label="Número" type="text" bind:value={entity.addressNumber}></Zinput>
                    <Zinput size={5} sizeM={12} label="Complemento" type="text" bind:value={entity.addressComplement}></Zinput>
                    <Zinput size={4} sizeM={12} label="Bairro" type="text" bind:value={entity.addressNeighborhood}></Zinput>
                    <Zinput size={8} sizeM={12} label="Cidade" type="text" bind:value={entity.addressCity}></Zinput>
                    <Zinput size={4} sizeM={12} label="Estado" type="text" bind:value={entity.addressState}></Zinput>
                </ZContainer>

                {#if (payer.immutableFields || []).length > 0}
                    <div class="alert alert-warning mt-3 mb-0">
                        O gateway desta cobrança não permite alterar: {rotulos(payer.immutableFields)}.
                    </div>
                {/if}
                {#if unicaChance(payer).length > 0}
                    <div class="alert alert-primary mt-3 mb-0">
                        Depois de salvo, o gateway desta cobrança não permite mais alterar: {rotulos(unicaChance(payer))}. Confira antes de salvar.
                    </div>
                {/if}
            </Zframe>
        {/if}
    {/if}
    <LoadSpinner {show} />
</div>
